/**
 * Condizioni Contrattuali Page
 * Terms and conditions for Artego Condo Manager subscriptions
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText, CheckCircle2, Euro, Shield, AlertTriangle, Scale, Clock } from 'lucide-react';

export default function ContractTerms() {
  return (
    <div className="artego-prose min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/workspace" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="h-5 w-5" />
            <span>Torna al Workspace</span>
          </Link>
          <div className="flex items-center gap-2">
            <Scale className="h-6 w-6 text-indigo-600" />
            <span className="font-semibold text-slate-900">ARTEGO.APP</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card className="shadow-lg">
          <CardContent className="p-6 lg:p-10">
            {/* Title */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
                <FileText className="h-8 w-8 text-indigo-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Condizioni Contrattuali</h1>
              <p className="text-slate-600">Termini e condizioni per l'utilizzo dei servizi ARTEGO.APP</p>
              <p className="text-sm text-slate-500 mt-2">Versione 1.0 - Gennaio 2026</p>
            </div>

            {/* Content */}
            <div className="prose prose-slate max-w-none">
              
              {/* Section 1 - Definizioni */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">1</span>
                  Definizioni
                </h2>
                <div className="bg-slate-50 rounded-lg p-4 space-y-3 text-sm">
                  <p><strong>"Fornitore"</strong>: ARTEGO SRLS, con sede in Via M.se di Villabianca, 114 - 90143 Palermo, P.IVA 07221580827</p>
                  <p><strong>"Cliente"</strong>: il soggetto che sottoscrive un abbonamento ai Servizi</p>
                  <p><strong>"Servizio"</strong>: la piattaforma ARTEGO.APP per la gestione condominiale</p>
                  <p><strong>"Workspace"</strong>: l'ambiente di lavoro dedicato al Cliente sulla piattaforma</p>
                  <p><strong>"Abbonamento"</strong>: il contratto di utilizzo del Servizio secondo uno dei piani disponibili</p>
                </div>
              </section>

              {/* Section 2 - Oggetto */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">2</span>
                  Oggetto del Contratto
                </h2>
                <p className="text-slate-700 mb-4">
                  Le presenti condizioni regolano l'utilizzo della piattaforma ARTEGO.APP, un software 
                  in modalità SaaS (Software as a Service) per la gestione amministrativa dei condomini, 
                  che include:
                </p>
                <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
                  <li>Gestione anagrafica condominiale e unità immobiliari</li>
                  <li>Contabilità condominiale e gestione spese</li>
                  <li>Comunicazioni con condomini e fornitori</li>
                  <li>Archiviazione e gestione documentale</li>
                  <li>Gestione assemblee e verbali</li>
                  <li>Reportistica e adempimenti fiscali</li>
                </ul>
              </section>

              {/* Section 3 - Piani di Abbonamento */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">3</span>
                  Piani di Abbonamento
                </h2>
                <p className="text-slate-700 mb-4">
                  Il Servizio è disponibile nei seguenti piani di abbonamento:
                </p>
                
                {/* Plan Cards */}
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  {/* BASIC Plan */}
                  <div className="border-2 border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-lg text-slate-900">Piano BASIC</h3>
                      <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded">Entry Level</span>
                    </div>
                    <p className="text-slate-600 text-sm mb-3">Per piccoli amministratori e condomini singoli</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span>Max 5 condomini</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span>Max 150 unità immobiliari</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span>Max 20 collaboratori</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span>5 GB di storage</span>
                      </div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-slate-200">
                      <p className="font-semibold text-slate-900">€49/mese + IVA</p>
                      <p className="text-sm text-slate-500">oppure €490/anno + IVA (risparmio 17%)</p>
                    </div>
                  </div>

                  {/* PRO Plan */}
                  <div className="border-2 border-indigo-500 rounded-lg p-4 bg-indigo-50/50">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-lg text-slate-900">Piano PRO</h3>
                      <span className="bg-indigo-600 text-white text-xs px-2 py-1 rounded">Professional</span>
                    </div>
                    <p className="text-slate-600 text-sm mb-3">Per studi di amministrazione professionale</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span>Max 20 condomini</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span>Max 2.000 unità immobiliari</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span>Max 80 collaboratori</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span>50 GB di storage</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-indigo-500" />
                        <span className="text-indigo-700">Accesso API + Supporto prioritario</span>
                      </div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-indigo-200">
                      <p className="font-semibold text-slate-900">€149/mese + IVA</p>
                      <p className="text-sm text-slate-500">oppure €1.490/anno + IVA (risparmio 17%)</p>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-800">Prova Gratuita</p>
                      <p className="text-sm text-amber-700">
                        È disponibile una prova gratuita di 10 giorni con accesso a tutte le funzionalità 
                        (max 3 condomini, 50 unità, 1 GB storage). Al termine del periodo di prova, 
                        sarà necessario sottoscrivere un abbonamento per continuare ad utilizzare il Servizio.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 4 - Durata e Rinnovo */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">4</span>
                  Durata e Rinnovo
                </h2>
                
                <div className="space-y-4">
                  <div className="border-l-4 border-indigo-500 pl-4">
                    <h3 className="font-semibold text-slate-900">4.1 Durata</h3>
                    <p className="text-slate-600 text-sm">
                      L'abbonamento ha durata mensile o annuale, a seconda dell'opzione scelta dal Cliente 
                      al momento della sottoscrizione. Il periodo decorre dalla data di attivazione.
                    </p>
                  </div>
                  
                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="font-semibold text-slate-900">4.2 Rinnovo Automatico</h3>
                    <p className="text-slate-600 text-sm">
                      L'abbonamento si rinnova automaticamente alla scadenza, salvo disdetta comunicata 
                      almeno 15 giorni prima della scadenza. Il Cliente può disattivare il rinnovo automatico 
                      in qualsiasi momento dalle impostazioni del Workspace.
                    </p>
                  </div>
                  
                  <div className="border-l-4 border-amber-500 pl-4">
                    <h3 className="font-semibold text-slate-900">4.3 Modifica Piano</h3>
                    <p className="text-slate-600 text-sm">
                      Il Cliente può richiedere l'upgrade a un piano superiore in qualsiasi momento. 
                      L'upgrade è immediato e verrà addebitata la differenza pro-rata. Il downgrade 
                      sarà effettivo dal successivo periodo di fatturazione.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 5 - Pagamenti */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">5</span>
                  Modalità di Pagamento
                </h2>
                
                <p className="text-slate-700 mb-4">
                  Il pagamento dell'abbonamento può essere effettuato mediante:
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Euro className="h-5 w-5 text-slate-600" />
                      <h3 className="font-semibold text-slate-900">Bonifico Bancario</h3>
                    </div>
                    <p className="text-sm text-slate-600">
                      IBAN: IT47K0200804692000107339318<br />
                      Banca: UNICREDIT SPA<br />
                      Intestatario: ARTEGO SRLS
                    </p>
                  </div>
                  
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="h-5 w-5 text-slate-600" />
                      <h3 className="font-semibold text-slate-900">Carta di Credito</h3>
                    </div>
                    <p className="text-sm text-slate-600">
                      Pagamento sicuro tramite gateway certificato PCI-DSS.<br />
                      Accettate: Visa, Mastercard, American Express
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Fatturazione:</strong> Tutti i prezzi sono da intendersi IVA esclusa (22%). 
                    La fattura elettronica sarà emessa e trasmessa tramite SDI entro 10 giorni dalla 
                    conferma del pagamento.
                  </p>
                </div>
              </section>

              {/* Section 6 - Obblighi del Cliente */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">6</span>
                  Obblighi del Cliente
                </h2>
                <p className="text-slate-700 mb-4">Il Cliente si impegna a:</p>
                <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
                  <li>Fornire dati veritieri e aggiornati in fase di registrazione</li>
                  <li>Mantenere riservate le credenziali di accesso</li>
                  <li>Utilizzare il Servizio in conformità alla normativa vigente</li>
                  <li>Non cedere a terzi le credenziali o l'accesso al Workspace</li>
                  <li>Effettuare i pagamenti entro le scadenze previste</li>
                  <li>Comunicare tempestivamente eventuali variazioni dei dati di fatturazione</li>
                </ul>
              </section>

              {/* Section 7 - Responsabilità */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">7</span>
                  Limitazione di Responsabilità
                </h2>
                <div className="space-y-4">
                  <p className="text-slate-700">
                    Il Fornitore garantisce una disponibilità del Servizio (uptime) pari al 99,5% su base annua, 
                    escluse le manutenzioni programmate comunicate con almeno 48 ore di anticipo.
                  </p>
                  <p className="text-slate-700">
                    Il Fornitore non sarà responsabile per:
                  </p>
                  <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
                    <li>Danni derivanti dall'uso improprio del Servizio da parte del Cliente</li>
                    <li>Perdita di dati causata da azioni del Cliente</li>
                    <li>Interruzioni dovute a cause di forza maggiore</li>
                    <li>Malfunzionamenti di servizi di terze parti</li>
                  </ul>
                  <p className="text-slate-700">
                    In ogni caso, la responsabilità del Fornitore è limitata all'importo pagato dal Cliente 
                    nei 12 mesi precedenti l'evento dannoso.
                  </p>
                </div>
              </section>

              {/* Section 8 - Recesso e Risoluzione */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">8</span>
                  Recesso e Risoluzione
                </h2>
                
                <div className="space-y-4">
                  <div className="border-l-4 border-indigo-500 pl-4">
                    <h3 className="font-semibold text-slate-900">8.1 Recesso del Cliente</h3>
                    <p className="text-slate-600 text-sm">
                      Il Cliente può recedere dall'abbonamento in qualsiasi momento disattivando il rinnovo 
                      automatico. Il Servizio rimarrà attivo fino alla scadenza del periodo già pagato. 
                      Non sono previsti rimborsi per periodi parziali.
                    </p>
                  </div>
                  
                  <div className="border-l-4 border-red-500 pl-4">
                    <h3 className="font-semibold text-slate-900">8.2 Risoluzione per Inadempimento</h3>
                    <p className="text-slate-600 text-sm">
                      Il Fornitore può risolvere il contratto con effetto immediato in caso di:
                    </p>
                    <ul className="text-slate-600 text-sm list-disc list-inside ml-4 mt-2">
                      <li>Mancato pagamento entro 30 giorni dalla scadenza</li>
                      <li>Violazione delle presenti condizioni</li>
                      <li>Utilizzo del Servizio per attività illecite</li>
                    </ul>
                  </div>
                  
                  <div className="border-l-4 border-amber-500 pl-4">
                    <h3 className="font-semibold text-slate-900">8.3 Effetti della Cessazione</h3>
                    <p className="text-slate-600 text-sm">
                      Alla cessazione del contratto, il Cliente avrà 30 giorni per esportare i propri dati. 
                      Trascorso tale termine, i dati saranno definitivamente cancellati.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 9 - Privacy */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">9</span>
                  Trattamento dei Dati Personali
                </h2>
                <p className="text-slate-700">
                  Il trattamento dei dati personali è regolato dalla nostra{' '}
                  <Link to="/privacy" className="text-indigo-600 hover:underline">Privacy Policy</Link>, 
                  che costituisce parte integrante delle presenti condizioni. Il Cliente, in qualità di 
                  Titolare del trattamento dei dati dei condomini inseriti nella piattaforma, nomina il 
                  Fornitore quale Responsabile del trattamento ai sensi dell'art. 28 del GDPR.
                </p>
              </section>

              {/* Section 10 - Proprietà Intellettuale */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">10</span>
                  Proprietà Intellettuale
                </h2>
                <p className="text-slate-700">
                  ARTEGO.APP, il suo codice sorgente, la grafica, i contenuti e tutti i materiali correlati 
                  sono di proprietà esclusiva di ARTEGO SRLS. L'abbonamento non trasferisce al Cliente alcun 
                  diritto di proprietà intellettuale sul Servizio, ma solo una licenza d'uso limitata, 
                  non esclusiva e non trasferibile per la durata dell'abbonamento.
                </p>
              </section>

              {/* Section 11 - Modifiche */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">11</span>
                  Modifiche alle Condizioni
                </h2>
                <p className="text-slate-700">
                  Il Fornitore si riserva il diritto di modificare le presenti condizioni. Le modifiche 
                  saranno comunicate via email almeno 30 giorni prima della loro entrata in vigore. 
                  Il proseguimento dell'utilizzo del Servizio dopo tale data costituisce accettazione 
                  delle nuove condizioni.
                </p>
              </section>

              {/* Section 12 - Legge Applicabile */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">12</span>
                  Legge Applicabile e Foro Competente
                </h2>
                <p className="text-slate-700">
                  Le presenti condizioni sono regolate dalla legge italiana. Per qualsiasi controversia 
                  derivante dal presente contratto sarà competente in via esclusiva il Foro di Palermo, 
                  salvo diversa disposizione inderogabile di legge.
                </p>
              </section>

              {/* Section 13 - Contatti */}
              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">13</span>
                  Contatti
                </h2>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="mb-2"><strong>ARTEGO SRLS</strong></p>
                  <p className="text-slate-600 text-sm">
                    Via M.se di Villabianca, 114<br />
                    90143 Palermo (PA) - Italia<br />
                    P.IVA: 07221580827<br />
                    Email: <a href="mailto:info@artegosrls.it" className="text-indigo-600 hover:underline">info@artegosrls.it</a><br />
                    PEC: <a href="mailto:artegosrls@pec.it" className="text-indigo-600 hover:underline">artegosrls@pec.it</a><br />
                    Assistenza: <a href="mailto:support.app@artegosrls.it" className="text-indigo-600 hover:underline">support.app@artegosrls.it</a>
                  </p>
                </div>
              </section>

            </div>

            {/* Footer Actions */}
            <div className="mt-10 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-slate-500">
                <Clock className="h-5 w-5" />
                <span className="text-sm">Versione 1.0 - Gennaio 2026</span>
              </div>
              <div className="flex gap-3">
                <Link to="/privacy">
                  <Button variant="outline">
                    <Shield className="h-4 w-4 mr-2" />
                    Privacy Policy
                  </Button>
                </Link>
                <Link to="/workspace/status">
                  <Button>
                    <Euro className="h-4 w-4 mr-2" />
                    Abbonati
                  </Button>
                </Link>
              </div>
            </div>

          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-8 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-slate-500">
          <p>© 2026 Artego SRLS - Tutti i diritti riservati</p>
          <p className="mt-1">Via M.se di Villabianca, 114 - 90143 Palermo</p>
        </div>
      </footer>
    </div>
  );
}
